import { type FooterLinkGroup } from './Footer';

/**
 * Groups footer link sections into columns, combining smaller groups together
 * without exceeding the size of the largest group.
 *
 * This is useful for visually balancing footer columns by ensuring that
 * smaller sections are grouped into the same column if their combined number
 * of items does not exceed the number of items in the largest section.
 *
 * For example, if the largest group has 10 items, two smaller groups with 4
 * and 5 items will be grouped together, but a group with 7 items will start
 * a new column.
 *
 * @param groups - An array of FooterLinkGroup objects to organize into columns.
 * @returns A nested array where each inner array represents a column of grouped sections.
 */
export const groupFooterSections = (groups: FooterLinkGroup[]): FooterLinkGroup[][] => {
  const MAX_SIZE = Math.max(...groups.map((g) => g.items.length));

  return groups.reduce<FooterLinkGroup[][]>((acc, group) => {
    const groupSize = group.items.length;
    const lastGroup = acc[acc.length - 1];

    if (!lastGroup || lastGroup.reduce((sum, g) => sum + g.items.length, 0) + groupSize > MAX_SIZE) {
      return acc.concat([[group]]);
    }
    const newAcc = acc.slice(0, -1);
    const merged = lastGroup.concat(group);
    return newAcc.concat([merged]);
  }, []);
};
